import { ExclamationCircleFilled, FilePdfOutlined, PictureOutlined } from "@ant-design/icons";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { Button, Input, Modal, Popover, Spin, message as antdMessage } from "antd";
import { EasyAvatar } from "fin-web-components";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { globalModel } from "../../global/GlobalModel";
import { CompanionRoomDocumentGlyph, CompanionRoomMessageGlyph } from "./CompanionRoomHeaderGlyphs";
import { CompanionRoomMessageContent } from "./CompanionRoomMessageContent";
import { CompanionRoomModel } from "./CompanionRoomModel";
import {
  companionIsSelfModerationRejected,
  companionMessageIsMine,
  type CompanionMessageRow,
  type CompanionVisibility,
  type MyMessageThreadRow,
} from "./CompanionRoomType";
import { uploadAppFileToOss } from "../../global/OssUploadApi";
import { MemberNickname } from "../../components/member-badge/MemberNickname";
import type { MemberBadge } from "../../components/member-badge/MemberBadgeType";
import "./CompanionRoomStyle.css";
import { avatarDisplayWord } from "../../utils/avatarDisplayWord";

function CompanionSelfModerationRejectHint(props: { stopParentClick?: boolean }): JSX.Element {
  return (
    <Popover
      placement="topLeft"
      trigger="click"
      content={<div className="crm-moderation-reject-pop">该消息未通过审核，当前仅您自己可见。</div>}
    >
      <button
        type="button"
        className="crm-moderation-reject-hit"
        aria-label="审核未通过"
        onClick={props.stopParentClick ? (e) => e.stopPropagation() : undefined}
      >
        <ExclamationCircleFilled className="crm-moderation-reject-icon" />
      </button>
    </Popover>
  );
}

function formatChatTime(iso?: string | null): string {
  if (!iso?.trim()) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const mo = d.getMonth() + 1;
  const day = d.getDate();
  const hm = d.toLocaleTimeString("zh-CN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const y = d.getFullYear();
  const nowY = new Date().getFullYear();
  if (y !== nowY) return `${y}年${mo}月${day}日 ${hm}`;
  return `${mo}月${day}日 ${hm}`;
}

function cmpSentAtAsc(a: CompanionMessageRow, b: CompanionMessageRow): number {
  const ta = a.sentAt ? new Date(a.sentAt).getTime() : 0;
  const tb = b.sentAt ? new Date(b.sentAt).getTime() : 0;
  if (ta !== tb) return ta - tb;
  return cmpMsgId(a.id, b.id);
}

function cmpMsgId(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true });
}

function sortRepliesDesc(rows: CompanionMessageRow[]): CompanionMessageRow[] {
  return [...rows].sort((a, b) => -cmpSentAtAsc(a, b));
}

function sortRepliesAsc(rows: CompanionMessageRow[]): CompanionMessageRow[] {
  return [...rows].sort(cmpSentAtAsc);
}

function companionStaffRoleLabel(code?: string | null): string {
  const c = (code ?? "").trim().toLowerCase();
  if (c === "teacher") return "老师";
  if (c === "assistant") return "助理";
  return "";
}

/** API 缺失或旧数据兜底：产品上非「老师」即「助理」 */
function companionStaffRoleFallbackLabel(): string {
  return "助理";
}

function formatReplyVisibility(v?: CompanionVisibility | null): string {
  const raw = (v ?? "").trim().toUpperCase();
  if (raw === "PRIVATE") return "私信";
  return "公开";
}

function formatQuoteFooterText(senderLabel: string, body: string): string {
  const b = body.trim();
  return b ? `${senderLabel}: ${b}` : `${senderLabel}`;
}

function pinPreviewLine(m: CompanionMessageRow): string {
  const raw = (m.content ?? "").replace(/\s+/g, " ").trim();
  if (!raw) return "（置顶消息）";
  return raw.length > 120 ? `${raw.slice(0, 120)}…` : raw;
}

function pickImageFromClipboard(e: React.ClipboardEvent<HTMLElement>): File | null {
  const cd = e.clipboardData;
  const files = cd?.files ? Array.from(cd.files) : [];
  for (const f of files) {
    if (f.type.startsWith("image/")) return f;
  }
  const items = cd?.items ? Array.from(cd.items) : [];
  for (const item of items) {
    if (item.kind !== "file") continue;
    const f = item.getAsFile();
    if (f && f.type.startsWith("image/")) return f;
  }
  return null;
}

type ComposeReply = {
  messageId: string;
  senderLabel: string;
  quotedFull: string;
  /** 引用自己发过的话时，正文前缀用「引用」而非「回复」 */
  quoteSelf?: boolean;
};

type ComposeImageDraft = {
  fileUrl: string;
  fileName: string;
};

function quotedSnippet(m: CompanionMessageRow): string {
  if (m.messageType === "TEXT") return m.content ?? "";
  if (m.messageType === "IMAGE") return "[图片]";
  if (m.messageType === "PDF") return "[文件]";
  return m.content ?? "";
}

const CompanionRoomPage: React.FC<{
  spaceId: string;
  spaceProductId?: string;
  productDisplayName?: string;
  spaceDisplayName?: string;
  membershipDaysRemaining?: number | null;
  subscribeHomePath?: string;
  myMemberBadges?: MemberBadge[] | null;
}> = observer(
  ({
    spaceId,
    spaceProductId,
    productDisplayName,
    spaceDisplayName,
    membershipDaysRemaining,
    subscribeHomePath,
    myMemberBadges,
  }) => {
    const navigate = useNavigate();
    const model = useMemo(() => new CompanionRoomModel(), []);
  const scrollRef = useRef<HTMLDivElement>(null);
  const mineSideScrollRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const [draft, setDraft] = useState("");
  const [composeReply, setComposeReply] = useState<ComposeReply | null>(null);
  const [composeImage, setComposeImage] = useState<ComposeImageDraft | null>(null);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [pdfPreview, setPdfPreview] = useState<{ url: string; title: string } | null>(null);
  const [threadDetail, setThreadDetail] = useState<MyMessageThreadRow | null>(null);

  useEffect(() => {
    if (!globalModel.isLoggedIn || !spaceId.trim() || !spaceProductId?.trim()) {
      return;
    }
    void model.init(spaceId.trim(), globalModel.currentUser?.id ?? "", spaceProductId.trim());
    return () => {
      model.dispose();
    };
  }, [spaceId, spaceProductId, model, globalModel.isLoggedIn, globalModel.currentUser?.id]);

  const scrollChatToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  /** 发消息/列表变化后补几次滚底，避免布局未完成时贴不紧 */
  const forceScrollChatToBottom = useCallback(() => {
    scrollChatToBottom();
    requestAnimationFrame(scrollChatToBottom);
    window.setTimeout(scrollChatToBottom, 50);
    window.setTimeout(scrollChatToBottom, 120);
  }, [scrollChatToBottom]);

  const chatLen = model.chatMessages.length;
  useEffect(() => {
    forceScrollChatToBottom();
  }, [chatLen, model.sidePanel, forceScrollChatToBottom]);

  const onChatScroll = (): void => {
    const el = scrollRef.current;
    if (!el || model.loadingOlderChat || !model.hasOlderChat) return;
    if (el.scrollTop < 48) {
      const prevHeight = el.scrollHeight;
      void model.loadOlderChat().then(() => {
        requestAnimationFrame(() => {
          const next = scrollRef.current;
          if (next) next.scrollTop = next.scrollHeight - prevHeight;
        });
      });
    }
  };

  const jumpMineToLatest = async (): Promise<void> => {
    await model.loadMineFirstPage();
    requestAnimationFrame(() => {
      mineSideScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    });
  };

  const buildTextPayload = (): string => {
    const t = draft.trim();
    /** 引用上下文由 replyToMessageId 表达，不在正文重复拼接 */
    return t;
  };

  const sendComposer = async (): Promise<void> => {
    if (!model.chatOpen) {
      antdMessage.warning("当前不可发言");
      return;
    }
    if (composeImage) {
      await model.sendImage({
        fileUrl: composeImage.fileUrl,
        fileName: composeImage.fileName,
        replyToMessageId: composeReply?.messageId,
      });
      setComposeImage(null);
      setComposeReply(null);
      forceScrollChatToBottom();
      return;
    }
    const payload = buildTextPayload();
    if (!payload.trim()) {
      antdMessage.warning("请输入内容");
      return;
    }
    await model.sendText({
      content: payload,
      replyToMessageId: composeReply?.messageId,
    });
    setDraft("");
    setComposeReply(null);
    forceScrollChatToBottom();
  };

  const uploadImageFile = async (f: File): Promise<void> => {
    if (composeImage) {
      antdMessage.warning("已选择图片，请先移除后再添加");
      return;
    }
    setUploadingImg(true);
    try {
      const url = await uploadAppFileToOss(f, 'COMPANION_CHAT_IMAGE');
      setComposeImage({
        fileUrl: url,
        fileName: f.name.trim() || "图片",
      });
    } finally {
      setUploadingImg(false);
    }
  };

  const onPickImage = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !f.type.startsWith("image/")) return;
    await uploadImageFile(f);
  };

  const onComposePaste = (e: React.ClipboardEvent<HTMLElement>): void => {
    const f = pickImageFromClipboard(e);
    if (!f) return;
    e.preventDefault();
    void uploadImageFile(f);
  };

  const selfUserId = globalModel.currentUser?.id ?? "";

  const startReply = (m: CompanionMessageRow): void => {
    if (Boolean(m.deleted) || Boolean(m.shielded)) return;
    const mine = companionMessageIsMine(m, selfUserId);
    if (mine && companionIsSelfModerationRejected(m)) return;
    if (mine && (Boolean(m.outboundFailed) || model.hasUndeliveredOutboundRetry(m.id))) {
      return;
    }
    const label = m.senderName?.trim() || (mine ? "我" : m.fromUser ? "用户" : "老师");
    setComposeReply({
      messageId: m.id,
      senderLabel: label,
      quotedFull: quotedSnippet(m).trim(),
      quoteSelf: mine,
    });
  };

  const pinnedListNewestFirst = useMemo(() => [...model.pinnedList].reverse(), [model.pinnedList]);
  const latestPinned =
    model.pinnedList.length > 0 ? model.pinnedList[model.pinnedList.length - 1]! : null;

  const showRenewHint =
    typeof membershipDaysRemaining === "number" &&
    membershipDaysRemaining >= 0 &&
    membershipDaysRemaining < 7;

  const renderChatMessage = (m: CompanionMessageRow): JSX.Element => {
    const self = companionMessageIsMine(m, selfUserId);
    const isStaff = !Boolean(m.fromUser);
    const name = m.senderName?.trim() || (self ? "我" : isStaff ? "老师" : "用户");
    const selfModRejected = self && companionIsSelfModerationRejected(m);
    const showOutboundFail = self && Boolean(m.outboundFailed);
    const ownUndelivered =
      self &&
      (Boolean(m.outboundFailed) || model.hasUndeliveredOutboundRetry(m.id));
    const canReply =
      model.chatOpen && !m.deleted && !m.shielded && !ownUndelivered && !selfModRejected;
    const timeStr = formatChatTime(m.sentAt);
    return (
      <div key={m.id} className={`crm-msg-row ${self ? "crm-msg-row--self" : ""}`}>
        <EasyAvatar
          shape="square"
          squareRadius={6}
          size={36}
          url={undefined}
          word={
            self ? "我" : avatarDisplayWord(m.senderName?.trim() || (isStaff ? "老师" : "用户"), m.senderUserId)
          }
          alt=""
          className="crm-avatar"
        />
        <div className="crm-msg-col">
          {self ? (
            <div className="crm-msg-head crm-msg-head--self">
              <span className="crm-msg-name">
                <MemberNickname nickname="我" badges={myMemberBadges} />
              </span>
              {timeStr ? <span className="crm-msg-time">{timeStr}</span> : null}
            </div>
          ) : (
            <div className="crm-msg-head">
              <span className="crm-msg-name">{name}</span>
              {isStaff ? (
                <span className="crm-msg-role">
                  {companionStaffRoleLabel(m.senderSpaceRole) || companionStaffRoleFallbackLabel()}
                </span>
              ) : null}
              {timeStr ? <span className="crm-msg-time">{timeStr}</span> : null}
            </div>
          )}
          <div className={`crm-msg-body-line ${self ? "crm-msg-body-line--self" : ""}`}>
            {showOutboundFail ? (
              <Popover
                placement="topLeft"
                trigger="click"
                content={
                  <div className="crm-outbound-fail-pop">
                    <p className="crm-outbound-fail-pop-title">发送失败</p>
                    <button
                      type="button"
                      className="crm-outbound-fail-retry"
                      onClick={() => void model.retryOutboundMessage(m.id)}
                    >
                      重新发送
                    </button>
                  </div>
                }
              >
                <button type="button" className="crm-outbound-fail-hit" aria-label="发送失败">
                  <ExclamationCircleFilled className="crm-outbound-fail-icon" />
                </button>
              </Popover>
            ) : null}
            {selfModRejected ? <CompanionSelfModerationRejectHint /> : null}
            {canReply && self ? (
              <div className="crm-msg-side-actions crm-msg-side-actions--before-bubble">
                <button type="button" className="crm-linkish" onClick={() => startReply(m)} aria-label="引用本条消息">
                  回复
                </button>
              </div>
            ) : null}
            <div className="crm-msg-bubble-wrap">
              <div className={`crm-msg-bubble ${self ? "crm-msg-bubble--self" : "crm-msg-bubble--other"}`}>
                <CompanionRoomMessageContent
                  m={m}
                  onOpenPdf={(url, name) => setPdfPreview({ url, title: name })}
                />
              </div>
            </div>
            {canReply && !self ? (
              <div className="crm-msg-side-actions">
                <button type="button" className="crm-linkish" onClick={() => startReply(m)}>
                  回复
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  const headerTitle =
    model.roomTitle?.trim() ||
    productDisplayName?.trim() ||
    spaceDisplayName?.trim() ||
    "聊天室";

  const sendDisabled =
    !model.chatOpen || model.sending || (!composeImage && !draft.trim());

  if (!globalModel.isLoggedIn) {
    return (
      <div className="crm-page">
        <div className="crm-empty">请先登录后查看聊天室。</div>
      </div>
    );
  }

  if (model.loading && !model.roomEnabled && !model.loadError) {
    return (
      <div className="crm-page">
        <div className="crm-empty">
          <Spin />
        </div>
      </div>
    );
  }

  if (model.loadError) {
    return (
      <div className="crm-page">
        <div className="crm-empty">加载失败，请稍后重试。</div>
      </div>
    );
  }

  if (!model.roomEnabled) {
    return (
      <div className="crm-page">
        <div className="crm-empty">当前空间未开通聊天室功能。</div>
      </div>
    );
  }

  if (!model.chatRoomId) {
    return (
      <div className="crm-page">
        <div className="crm-empty">管理员暂未创建文字直播房间，请稍后再试。</div>
      </div>
    );
  }

  const sidePanelAria =
    model.sidePanel === "mine"
      ? "我的发言"
      : model.sidePanel === "files"
        ? "学习资料"
        : model.sidePanel === "pinned"
          ? "置顶消息"
          : "";

  const pinnedStripEl = latestPinned ? (
    <button
      type="button"
      className="crm-pin-strip"
      aria-label="查看置顶消息"
      onClick={() => model.toggleSidePanel("pinned")}
    >
      <span className="crm-pin-strip-label">置顶</span>
      <span className="crm-pin-strip-text">{pinPreviewLine(latestPinned)}</span>
    </button>
  ) : null;

  const chatPanelSection = (
    <div className="crm-panel crm-panel--chat">
      <div ref={scrollRef} className="crm-chat-scroll" onScroll={onChatScroll}>
        {model.loadingOlderChat ? (
          <div className="crm-chat-loading-top">加载更早消息...</div>
        ) : null}
        {model.chatMessages.map((m) => renderChatMessage(m))}
      </div>
      <div className="crm-compose">
        <div
          className="crm-compose-box"
          onPasteCapture={model.chatOpen ? onComposePaste : undefined}
        >
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            className="crm-compose-file-input"
            onChange={onPickImage}
          />
          <div className="crm-compose-toolbar-row">
            <Button
              type="text"
              icon={<PictureOutlined />}
              className="crm-compose-img-btn"
              loading={uploadingImg}
              disabled={!model.chatOpen || model.sending || Boolean(composeImage)}
              onClick={() => imageInputRef.current?.click()}
              aria-label="选择图片"
            />
            {model.maxPerDay != null && model.maxPerDay > 0 ? (
              <span className="crm-compose-quota">今日还剩{model.remainPerDay ?? 0}次发言</span>
            ) : null}
            {model.maxImagePerDay != null && model.maxImagePerDay > 0 ? (
              <span className="crm-compose-quota">图片还剩{model.remainImagePerDay ?? 0}次</span>
            ) : null}
          </div>
          <div className="crm-compose-type-wrap">
            <div
              className={`crm-compose-textarea-shell${
                composeReply ? " crm-compose-textarea-shell--quote-active" : ""
              }`}
            >
              {composeImage ? (
                <div className="crm-compose-attachment-preview">
                  <div className="crm-compose-attachment-preview-media">
                    <button
                      type="button"
                      className="crm-compose-attachment-remove"
                      onClick={() => setComposeImage(null)}
                    >
                      移除
                    </button>
                    <button
                      type="button"
                      className="crm-compose-preview-img-btn"
                      onClick={() =>
                        window.open(composeImage.fileUrl, "_blank", "noopener,noreferrer")
                      }
                    >
                      <img src={composeImage.fileUrl} alt="" className="crm-compose-preview-img" />
                    </button>
                  </div>
                </div>
              ) : (
                <Input.TextArea
                  className="crm-compose-area-inner"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder={model.chatOpen ? "输入消息…" : "当前不可发言"}
                  autoSize={{ minRows: 2, maxRows: 6 }}
                  disabled={!model.chatOpen}
                />
              )}
              {!composeReply ? (
                <Button
                  type="primary"
                  className="crm-compose-send-float"
                  loading={model.sending}
                  disabled={sendDisabled}
                  onClick={() => void sendComposer()}
                >
                  发送
                </Button>
              ) : null}
            </div>
            {composeReply ? (
              <div className="crm-compose-quote-send-row">
                <div className="crm-compose-footer-quote">
                  <div className="crm-quote-footer-rail" aria-hidden />
                  <div className="crm-quote-footer-main">
                    <span
                      className="crm-quote-footer-text"
                      title={`${composeReply.senderLabel}: ${composeReply.quotedFull}`}
                    >
                      {formatQuoteFooterText(composeReply.senderLabel, composeReply.quotedFull)}
                    </span>
                    <button
                      type="button"
                      className="crm-quote-inline-close"
                      aria-label="取消引用"
                      onClick={() => setComposeReply(null)}
                    >
                      ×
                    </button>
                  </div>
                </div>
                <Button
                  type="primary"
                  className="crm-compose-send-float crm-compose-send-inline"
                  loading={model.sending}
                  disabled={sendDisabled}
                  onClick={() => void sendComposer()}
                >
                  发送
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`crm-page crm-page--fill${model.sidePanel ? " crm-page--with-side" : ""}`}>
      <div className="crm-split crm-split--solo">
        <div className="crm-split-main">
          <div className={`crm-chat-shell${model.sidePanel ? " crm-chat-shell--with-side" : ""}`}>
            <div className="crm-header">
              <div className="crm-header-left">
                <span className="crm-header-title">{headerTitle}</span>
                {showRenewHint ? (
                  <span className="crm-membership-renew-inline">
                    {membershipDaysRemaining === 0 ? "会员今日到期" : `会员将于${membershipDaysRemaining}天后到期`}
                    {subscribeHomePath?.trim() ? (
                      <>
                        ，
                        <button
                          type="button"
                          className="crm-membership-renew-action"
                          onClick={() => void navigate(subscribeHomePath.trim())}
                        >
                          立即续费
                        </button>
                      </>
                    ) : null}
                  </span>
                ) : null}
                {!model.chatOpen ? <span className="crm-header-status">当前暂停发言</span> : null}
              </div>
              <div className="crm-header-actions">
                <button
                  type="button"
                  className={`crm-header-icon-btn${model.sidePanel === "mine" ? " crm-header-icon-btn--on" : ""}`}
                  aria-label="我的发言"
                  title="我的发言"
                  onClick={() => model.toggleSidePanel("mine")}
                >
                  <CompanionRoomMessageGlyph className="crm-header-icon-btn__glyph" />
                </button>
                <button
                  type="button"
                  className={`crm-header-icon-btn${model.sidePanel === "files" ? " crm-header-icon-btn--on" : ""}`}
                  aria-label="学习资料"
                  title="学习资料"
                  onClick={() => model.toggleSidePanel("files")}
                >
                  <CompanionRoomDocumentGlyph className="crm-header-icon-btn__glyph" />
                </button>
              </div>
            </div>

            {model.sidePanel ? (
              <div className="crm-side-panel-head">
                <p className="crm-side-panel-title" id="crm-companion-side-heading">
                  {sidePanelAria}
                </p>
                {model.sidePanel === "mine" ? (
                  <button
                    type="button"
                    className="crm-side-panel-jump-latest"
                    aria-label="看最新发言"
                    disabled={model.mineLoading}
                    onClick={() => void jumpMineToLatest()}
                  >
                    看最新
                  </button>
                ) : null}
              </div>
            ) : null}

            {!model.sidePanel ? (
              <>
                {pinnedStripEl}
                {chatPanelSection}
              </>
            ) : (
              <>
                <div className="crm-chat-primary-body">
                  {pinnedStripEl}
                  {chatPanelSection}
                </div>
                <aside
                  className="crm-side-panel-body-slot"
                  aria-labelledby="crm-companion-side-heading"
                >
                  {model.sidePanel === "mine" ? (
                    <div
                      ref={mineSideScrollRef}
                      className="crm-side-panel-body crm-side-panel-body--mine"
                    >
                      <Spin spinning={model.mineLoading}>
                        {model.mineThreads.map((th) => {
                          const repliesAsc = sortRepliesAsc(th.replies ?? []);
                          const rc =
                            typeof th.replyCount === "number" ? th.replyCount : repliesAsc.length;
                          return (
                            <button
                              key={th.myMessage.id}
                              type="button"
                              className="crm-mine-thread"
                              onClick={() => setThreadDetail(th)}
                            >
                              <div className="crm-mine-thread-inner">
                                <div className="crm-moments-post">
                                  <div className="crm-moments-post-head">
                                    <EasyAvatar
                                      shape="square"
                                      squareRadius={5}
                                      size={18}
                                      url={undefined}
                                      word={
                                        th.myMessage.fromUser
                                          ? "我"
                                          : avatarDisplayWord(
                                              th.myMessage.senderName?.trim() || "老师",
                                              th.myMessage.senderUserId
                                            )
                                      }
                                      alt=""
                                      className="crm-moments-avatar crm-moments-avatar--feed"
                                    />
                                    <div className="crm-moments-nickname">
                                      <MemberNickname nickname="我" badges={myMemberBadges} />
                                    </div>
                                    <span className="crm-moments-time">{formatChatTime(th.myMessage.sentAt)}</span>
                                  </div>
                                  <div
                                    className={
                                      companionIsSelfModerationRejected(th.myMessage)
                                        ? "crm-moments-content crm-moments-content-row"
                                        : "crm-moments-content"
                                    }
                                  >
                                    {companionIsSelfModerationRejected(th.myMessage) ? (
                                      <CompanionSelfModerationRejectHint stopParentClick />
                                    ) : null}
                                    <div className="crm-moments-content-row-inner">
                                      <CompanionRoomMessageContent
                                        m={th.myMessage}
                                        onOpenPdf={(url, name) =>
                                          setPdfPreview({ url, title: name })
                                        }
                                      />
                                    </div>
                                  </div>
                                </div>
                                {rc > 0 ? (
                                  <div className="crm-mine-replies-box">
                                    <ul className="crm-mine-reply-list">
                                        {repliesAsc.map((r) => {
                                          const tname = r.senderName?.trim() || "老师";
                                          const roleLabel =
                                            companionStaffRoleLabel(r.senderSpaceRole) ||
                                            companionStaffRoleFallbackLabel();
                                          const vis = formatReplyVisibility(r.visibility);
                                          const prv = vis === "私信";
                                          return (
                                            <li key={r.id} className="crm-mine-reply">
                                              <div className="crm-mine-reply-inner">
                                                <span className="crm-mine-reply-name">{tname}</span>
                                                <span className="crm-mine-role-badge">{roleLabel}</span>
                                                <span
                                                  className={
                                                    prv
                                                      ? "crm-mine-vis crm-mine-vis--private"
                                                      : "crm-mine-vis crm-mine-vis--public"
                                                  }
                                                >
                                                  {vis}
                                                </span>
                                                <span className="crm-meta-bullet" aria-hidden>
                                                  ·
                                                </span>
                                                <span className="crm-mine-reply-time">
                                                  {formatChatTime(r.sentAt)}
                                                </span>
                                              </div>
                                              <div className="crm-mine-reply-content">
                                                <CompanionRoomMessageContent
                                                  m={r}
                                                  onOpenPdf={(url, name) =>
                                                    setPdfPreview({ url, title: name })
                                                  }
                                                />
                                              </div>
                                            </li>
                                          );
                                        })}
                                      </ul>
                                  </div>
                                ) : null}
                              </div>
                            </button>
                          );
                        })}
                        {model.mineThreads.length === 0 && !model.mineLoading ? (
                          <div className="crm-empty">暂无发言记录</div>
                        ) : null}
                        {model.mineHasMore ? (
                          <div className="crm-mine-load-more-wrap">
                            <Button
                              className="crm-primary-btn"
                              loading={model.mineLoadingMore}
                              onClick={() => void model.loadMoreMine()}
                            >
                              加载更多
                            </Button>
                          </div>
                        ) : null}
                      </Spin>
                    </div>
                  ) : model.sidePanel === "files" ? (
                    <div className="crm-side-panel-body">
                      <Spin spinning={model.filesLoading}>
                        {model.pdfFiles.map((f) => (
                          <div key={f.messageId} className="crm-file-row">
                            <div className="crm-msg-head">
                              <span className="crm-msg-name">{f.senderName || "未知"}</span>
                              <span className="crm-msg-time">{formatChatTime(f.sentAt)}</span>
                            </div>
                            <div className="crm-msg-text">{f.fileName || "PDF"}</div>
                            <Button
                              type="primary"
                              className="crm-primary-btn crm-file-open"
                              onClick={() => setPdfPreview({ url: f.fileUrl, title: f.fileName || "PDF" })}
                            >
                              在线查看
                            </Button>
                          </div>
                        ))}
                        {model.pdfFiles.length === 0 && !model.filesLoading ? (
                          <div className="crm-empty">暂无 PDF 资料</div>
                        ) : null}
                        {model.filesHasMore ? (
                          <div style={{ textAlign: "center", padding: 12 }}>
                            <Button
                              className="crm-primary-btn"
                              loading={model.filesLoadingMore}
                              onClick={() => void model.loadMoreFiles()}
                            >
                              加载更多
                            </Button>
                          </div>
                        ) : null}
                      </Spin>
                    </div>
                  ) : (
                    <div className="crm-side-panel-body">
                      <Spin spinning={model.pinnedLoading}>
                        {pinnedListNewestFirst.map((m) => (
                          <div key={m.id} className="crm-pin-row">
                            <div className="crm-msg-head" style={{ marginBottom: 8 }}>
                              <span className="crm-msg-name">{m.senderName?.trim() || "老师"}</span>
                              <span className="crm-msg-time">{formatChatTime(m.sentAt)}</span>
                            </div>
                            <CompanionRoomMessageContent
                              m={m}
                              onOpenPdf={(url, name) => setPdfPreview({ url, title: name })}
                            />
                          </div>
                        ))}
                        {model.pinnedList.length === 0 && !model.pinnedLoading ? (
                          <div className="crm-empty">暂无置顶消息</div>
                        ) : null}
                      </Spin>
                    </div>
                  )}
                </aside>
              </>
            )}
          </div>
        </div>
      </div>

      <Modal
        title={pdfPreview?.title ?? "PDF"}
        open={pdfPreview != null}
        footer={null}
        width={900}
        centered
        className="crm-thread-modal"
        onCancel={() => setPdfPreview(null)}
        destroyOnClose
      >
        {pdfPreview ? (
          <iframe title={pdfPreview.title} className="pdf-viewer-frame" src={pdfPreview.url} />
        ) : null}
      </Modal>

      <Modal
        title="详情"
        open={threadDetail != null}
        footer={null}
        width={640}
        centered
        className="crm-thread-modal crm-thread-detail-modal"
        onCancel={() => setThreadDetail(null)}
        destroyOnClose
      >
        {threadDetail
          ? (() => {
              const threadRepliesDesc = sortRepliesDesc(threadDetail.replies ?? []);
              return (
                <div
                  className={
                    threadRepliesDesc.length === 0
                      ? "crm-moments-detail crm-moments-detail--no-replies"
                      : "crm-moments-detail"
                  }
                >
                  <div className="crm-moments-detail-post">
                    <EasyAvatar
                      shape="square"
                      squareRadius={5}
                      size={40}
                      url={undefined}
                      word={
                        threadDetail.myMessage.fromUser
                          ? "我"
                          : avatarDisplayWord(
                              threadDetail.myMessage.senderName?.trim() || "老师",
                              threadDetail.myMessage.senderUserId
                            )
                      }
                      alt=""
                      className="crm-moments-detail-avatar"
                    />
                    <div className="crm-moments-detail-main">
                      <div className="crm-moments-detail-head">
                        <div className="crm-moments-detail-nickname">
                          <MemberNickname nickname="我" badges={myMemberBadges} />
                        </div>
                        <span className="crm-moments-time">{formatChatTime(threadDetail.myMessage.sentAt)}</span>
                      </div>
                      <div
                        className={
                          companionIsSelfModerationRejected(threadDetail.myMessage)
                            ? "crm-moments-detail-content crm-moments-content-row"
                            : "crm-moments-detail-content"
                        }
                      >
                        {companionIsSelfModerationRejected(threadDetail.myMessage) ? (
                          <CompanionSelfModerationRejectHint />
                        ) : null}
                        <div className="crm-moments-content-row-inner">
                          <CompanionRoomMessageContent
                            m={threadDetail.myMessage}
                            onOpenPdf={(url, name) => setPdfPreview({ url, title: name })}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  {threadRepliesDesc.length > 0 ? (
                    <div className="crm-moments-interactions">
                      <div className="crm-moments-interactions-caption">老师回复</div>
                      <ul className="crm-moments-reply-lines">
                        {threadRepliesDesc.map((r) => {
                          const tname = r.senderName?.trim() || "老师";
                          const roleLabel =
                            companionStaffRoleLabel(r.senderSpaceRole) ||
                            companionStaffRoleFallbackLabel();
                          const vis = formatReplyVisibility(r.visibility);
                          const prv = vis === "私信";
                          return (
                            <li key={r.id} className="crm-moments-reply-line">
                              <div className="crm-moments-reply-head">
                                <span className="crm-mine-reply-name">{tname}</span>
                                <span className="crm-mine-role-badge">{roleLabel}</span>
                                <span
                                  className={
                                    prv
                                      ? "crm-mine-vis crm-mine-vis--private"
                                      : "crm-mine-vis crm-mine-vis--public"
                                  }
                                >
                                  {vis}
                                </span>
                                <span className="crm-meta-bullet" aria-hidden>
                                  ·
                                </span>
                                <span className="crm-mine-reply-time">
                                  {formatChatTime(r.sentAt)}
                                </span>
                              </div>
                              <div className="crm-moments-reply-body">
                                <CompanionRoomMessageContent
                                  m={r}
                                  onOpenPdf={(url, name) => setPdfPreview({ url, title: name })}
                                />
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ) : (
                    <div className="crm-moments-detail-empty-wrap">
                      <p className="crm-moments-detail-empty">暂无老师回复</p>
                    </div>
                  )}
                </div>
              );
            })()
          : null}
      </Modal>
    </div>
  );
});

export default CompanionRoomPage;
